import {
  LookingForSomethingElse,
  SectionHeader,
  publishEvent,
  useReadOnlyDataStreamer
} from '@uma/react-commons/dist/index';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FeatureFlagProps } from 'src/App';
import FrameCards from 'src/components/FrameCards';
import { useDependencyManagerContext } from 'src/contexts/dependencyManager';
import GET_HOME_SUBSCRIPTIONS from 'src/graphql/getHomeSubscriptions';
import { screenName, screenPath } from 'src/utils/constants';
import getFieldsToFetch from '../../utils/getFieldsToFetch';
import { SubscriptionsError, UserNameError } from './analytics';
import { Header } from './styles';

export interface PersonalDetailsType {
  firstName?: string;
  country?: string;
}

const isRetryableError = (errorMessage: string) => {
  if (errorMessage) {
    // Extract HTTP status code from error message using regex since ClientOS returns it in the message only
    // Example error message: "Request failed with status code 500"
    const statusCodeMatch = errorMessage.match(/\b(\d{3})\b/);

    if (statusCodeMatch) {
      const statusCode = parseInt(statusCodeMatch[1], 10);
      return (
        statusCode >= 500 || // 5xx: Server errors - temporary issues
        statusCode === 403 || // 403: Forbidden - insufficient permissions (e.g., token expired or invalid in some cases)
        statusCode === 401 // 401: Unauthorized - token issues
      );
    }
  }

  return false;
};

const Home = (featureFlagValue: FeatureFlagProps) => {
  const [hpIdUser, setHpIdUser] = useState<PersonalDetailsType>({
    firstName: '',
    country: ''
  });

  // Track refetch status
  // Use ref instead of state to avoid triggering re-renders
  const hasAttemptedRefetch = useRef(false);
  const [isRefetching, setIsRefetching] = useState(false);

  const { error: errorHpIdUser, loading: loadingHpIdUser } =
    useReadOnlyDataStreamer(setHpIdUser, getFieldsToFetch);

  const { translate, northboundAPIs } = useDependencyManagerContext();

  const { useQuery } = northboundAPIs.v1.graphql.reactTools;

  const {
    error: subscriptionsError,
    loading: isSubscriptionsLoading,
    data: subscriptionsData,
    refetch: refetchSubscriptions
  } = useQuery(GET_HOME_SUBSCRIPTIONS);

  const effectiveSubscriptionsLoading = isSubscriptionsLoading || isRefetching;

  const { subscriptions } = subscriptionsData?.account || {};

  const { isLoading, originalFeedbackExperience } = featureFlagValue;

  const showOriginalFeedbackExperience =
    originalFeedbackExperience && !isLoading;

  const welcomeText = useMemo(() => {
    if (loadingHpIdUser || !hpIdUser.firstName) {
      return translate('home.welcome', 'Welcome');
    }

    return translate('home.welcomeUser', {
      defaultValue: 'Welcome, {{user}}',
      replace: { user: hpIdUser.firstName }
    });
  }, [hpIdUser.firstName, loadingHpIdUser, translate]);

  useEffect(() => {
    if (subscriptionsError) {
      if (!hasAttemptedRefetch.current) {
        const shouldRetry = isRetryableError(subscriptionsError.message);

        if (shouldRetry) {
          hasAttemptedRefetch.current = true;
          console.error(
            'Subscriptions Cards (Home): Initial fetch failed, attempting to refetch subscriptions.',
            subscriptionsError.message
          );

          // Add delay for better UX and to avoid overwhelming server
          setTimeout(() => {
            setIsRefetching(true);
            refetchSubscriptions()
              .catch((refetchError: { message: string }) => {
                console.error(
                  'Subscriptions Cards (Home): Refetch Error:',
                  refetchError.message
                );
                publishEvent(SubscriptionsError(refetchError.message));
              })
              .finally(() => {
                setIsRefetching(false);
              });
          }, 1500);
        } else {
          console.error(
            'Subscriptions Cards (Home): Initial fetch failed with non-retryable error:',
            subscriptionsError.message
          );
          publishEvent(SubscriptionsError(subscriptionsError.message));
        }
      }
    }
  }, [refetchSubscriptions, subscriptionsError]);

  useEffect(() => {
    // Handle HPID user fetch errors
    if (errorHpIdUser) {
      const errorMsg = 'Error fetching field firstName from HPID';
      console.error('User Name (Home): ', errorMsg);
      publishEvent(UserNameError(errorMsg));
    }
  }, [errorHpIdUser]);

  return (
    <>
      <Header data-testid="header">
        <SectionHeader
          title={welcomeText}
          subtitle={translate(
            'home.mainTitle',
            'Quick links to your info, subscriptions and purchases with HP'
          )}
          dataTestidTitle={
            !loadingHpIdUser && hpIdUser.firstName ? 'username' : null
          }
        />
      </Header>
      <FrameCards
        subscriptions={subscriptions}
        isSubscriptionsLoading={effectiveSubscriptionsLoading}
        featureFlags={featureFlagValue}
      />
      {showOriginalFeedbackExperience && (
        <LookingForSomethingElse
          analytics={{
            screenName: screenName,
            screenPath: screenPath
          }}
        />
      )}
    </>
  );
};
export default Home;
